<template>
   <div :class="col">
      <div class="mb-0 position-relative sa-field">
         <!-- Label above the box (main.js formStyle 'icons' and 'plain') -->
         <label v-if="label && !floating" class="form-label">{{ label }}</label>

         <!-- $validateForm checks this hidden input (v-select has no real required box) -->
         <input ref="hidden" type="hidden" :name="name" :value="modelValue ?? ''" :required="required" />

         <div :class="wrapClass" @focusin="focused = true" @focusout="focused = false">
            <!-- Icon in front (formStyle 'icons' only) -->
            <span v-if="showIcon" class="input-group-text"><i :class="icon"></i></span>

            <v-select
               v-bind="$attrs"
               :options="options"
               :model-value="selectedOption"
               :label="labelKey"
               :filterable="false"
               :loading="loading"
               :placeholder="floating ? '' : placeholder"
               class="custom-tooltip-input validation-field"
               :class="[stateClass, { 'flex-grow-1': showIcon }]"
               @search="onSearch"
               @open="onOpen"
               @update:modelValue="onChange"
            >
               <!-- Shown inside the open list -->
               <template #no-options>
                  <span v-if="loading">Searching…</span>
                  <span v-else-if="error">{{ error }}</span>
                  <span v-else-if="searchText.trim().length < minChars">Type at least {{ minChars }} letter{{ minChars === 1 ? "" : "s" }} to search</span>
                  <span v-else>Nothing found for "{{ searchText.trim() }}"</span>
               </template>
            </v-select>

            <!-- Floating label (formStyle 'floating') -->
            <label v-if="floating" class="sa-float-label">{{ label }}</label>
         </div>

         <small v-if="hint" class="text-muted d-block">{{ hint }}</small>

         <!-- Above the box: on focus, and in red while the field is invalid -->
         <div v-if="tooltip" class="custom-tooltip">{{ tooltip }}<span class="tooltip-arrow"></span></div>
      </div>
   </div>
</template>

<script>
// A dropdown that searches the server while you type: for long lists (customers, products, suppliers ...).
//   <SearchSelectField v-model="formInputs.customer_id" col="col-md-6" label="Customer" icon="fa fa-user"
//                      api="/customers/api_customers.php" :selected-text="formInputs.customer_name"
//                      tooltip="Search by name or number" required />
// v-model = the chosen record's id (null when empty).
// api must answer GET ?action=searchOptions&search=...&limit=20 with results.options = [{ id, name, ... }]
// (sendSearchOptions() in global_functions.php does that in one line).
// selected-text: the name to show for the current id when editing (the list isn't loaded yet).
// label-key / value-key: other column names than "name" / "id". min-chars: letters before searching (default 1).
// @selected gives the whole chosen record, e.g. to fill in its price or phone.
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
   name: "SearchSelectField",
   components: { vSelect },
   inheritAttrs: false, // clearable, disabled ... go to v-select
   props: {
      modelValue: { default: null },
      api: { type: String, required: true },
      action: { type: String, default: "searchOptions" },
      labelKey: { type: String, default: "name" },
      valueKey: { type: String, default: "id" },
      selectedText: { type: String, default: "" },
      minChars: { type: Number, default: 1 },
      limit: { type: Number, default: 20 },
      label: { type: String, default: "" },
      name: { type: String, default: "" },
      icon: { type: String, default: "" },
      placeholder: { type: String, default: "Type to search" },
      tooltip: { type: String, default: "" },
      hint: { type: String, default: "" },
      col: { type: String, default: "col-md-6" },
      required: { type: Boolean, default: false },
   },
   emits: ["update:modelValue", "change", "selected"],
   data() {
      return {
         options: [],
         picked: null,      // the whole chosen record
         searchText: "",
         loading: false,
         error: "",
         focused: false,
         checked: false,    // true once $validateForm has checked this field (Save pressed)
         timer: null,
         requestNo: 0,      // only the newest search's answer is used
      };
   },
   computed: {
      formStyle() {
         return this.$formStyle || "icons";
      },
      floating() {
         return this.formStyle === "floating" && !!this.label;
      },
      showIcon() {
         return this.formStyle === "icons" && !!this.icon;
      },
      hasValue() {
         return this.modelValue !== null && this.modelValue !== undefined && this.modelValue !== "";
      },
      // What the box shows: the chosen record, or (editing) the id with its selected-text
      selectedOption() {
         if (!this.hasValue) return null;
         if (this.picked && String(this.picked[this.valueKey]) === String(this.modelValue)) return this.picked;
         const inList = this.options.find((o) => String(o[this.valueKey]) === String(this.modelValue));
         if (inList) return inList;
         return { [this.valueKey]: this.modelValue, [this.labelKey]: this.selectedText || `#${this.modelValue}` };
      },
      stateClass() {
         if (!this.required || !this.checked) return {};
         return { "is-valid": this.hasValue, "is-invalid": !this.hasValue };
      },
      wrapClass() {
         if (this.showIcon) return "input-group";
         if (this.floating) {
            return { "sa-float": true, "is-up": this.focused || this.hasValue, "is-focused": this.focused };
         }
         return "";
      },
   },
   mounted() {
      // Follow $validateForm / $clearValidationStates on the hidden input (red / green after Save)
      this.hiddenWatcher = new MutationObserver(() => {
         const classes = this.$refs.hidden?.classList;
         this.checked = !!classes && (classes.contains("is-invalid") || classes.contains("is-valid"));
      });
      if (this.$refs.hidden) {
         this.hiddenWatcher.observe(this.$refs.hidden, { attributes: true, attributeFilter: ["class"] });
      }
   },
   beforeUnmount() {
      this.hiddenWatcher?.disconnect();
      clearTimeout(this.timer);
   },
   methods: {
      // Typing: wait until the user pauses (300 ms), then ask the server
      onSearch(text) {
         this.searchText = text || "";
         this.error = "";
         clearTimeout(this.timer);
         if (this.searchText.trim().length < this.minChars) {
            this.options = [];
            this.loading = false;
            return;
         }
         this.loading = true;
         this.timer = setTimeout(() => this.fetchOptions(this.searchText.trim()), 300);
      },
      // Opening with no letters needed (min-chars = 0): show the first records straight away
      onOpen() {
         if (this.minChars === 0 && !this.options.length) this.fetchOptions("");
      },
      async fetchOptions(text) {
         const thisRequest = ++this.requestNo;
         this.loading = true;
         try {
            const url = `${this.api}?action=${encodeURIComponent(this.action)}&search=${encodeURIComponent(text)}&limit=${this.limit}`;
            const response = await axios.get(url, this.$handleConfig());
            if (thisRequest !== this.requestNo) return; // a newer search has started
            if (response.data.status === "success") {
               this.options = response.data.results.options || [];
            } else {
               this.options = [];
               this.error = response.data.message || "Could not search.";
            }
         } catch (e) {
            if (thisRequest !== this.requestNo) return;
            this.options = [];
            this.error = e && !e.response ? "No internet connection." : "Could not search. Please try again.";
         } finally {
            if (thisRequest === this.requestNo) this.loading = false;
         }
      },
      onChange(option) {
         this.picked = option || null;
         const value = option ? option[this.valueKey] : null;
         this.$emit("update:modelValue", value);
         this.$emit("change", value);
         this.$emit("selected", option || null);

         // Check the field straight away (green when picked, red when cleared)
         this.$nextTick(() => {
            if (this.$refs.hidden && typeof this.$validateField === "function") {
               this.$validateField(this.$refs.hidden);
            }
         });
      },
   },
};
</script>